import { IconEnvelopeCheckOutline18 as MailCheckIcon } from "nucleo-ui-outline-18"

import { Shortcut } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { useUndoContext } from "@/context/undo-context"
import { mutateOk, useZero } from "@/lib/zero"
import { useThreadContext } from "../../../thread-context"

type UndoChannel = "live-chat"

const channelByMessageId = new Map<string, UndoChannel>()

export const useMessageUndo = (onUndo?: (content: string) => void) => {
	const { setPendingMessage, removePendingMessage } = useUndoContext()
	const { thread } = useThreadContext()
	const zero = useZero()

	const getUndoMessageFn =
		(messageId: string, content?: string) => async () => {
			removePendingMessage(messageId)
			const channel = channelByMessageId.get(messageId)
			channelByMessageId.delete(messageId)
			if (channel === "live-chat") {
				await mutateOk(
					zero.mutate(mutators.liveChatMessages.delete({ id: messageId })),
				)
			} else if (thread.origin === "SLACK_CONNECT") {
				await mutateOk(
					zero.mutate(
						mutators.slackMessages.delete({
							id: messageId,
						}),
					),
				)
			} else {
				await mutateOk(
					zero.mutate(
						mutators.emails.undo({
							id: messageId,
						}),
					),
				)
			}

			// Restore content to editor if callback provided
			if (content && onUndo) {
				onUndo(content)
			}
		}

	const sendPendingMessage = (
		messageId: string,
		content: string,
		channel?: UndoChannel,
	) => {
		if (channel) channelByMessageId.set(messageId, channel)
		setPendingMessage(messageId, undefined, content)

		toast.undo({
			title: "Message sent",
			icon: MailCheckIcon,
			data: {
				messageId,
			},
			layout: "inline",
			time: 10000, // 10 seconds
			// onUndo wires Cmd+Z to the same cancel path as the button. Without it
			// Cmd+Z hit the editor's native undo (restoring draft text) while the
			// email stayed sent - it must delete the queued email, not just the text.
			onUndo: () => getUndoMessageFn(messageId, content)(),
			action: {
				label: "Undo",
				callback: () => {
					void getUndoMessageFn(messageId, content)()
				},
				shortcut: <Shortcut>Z</Shortcut>,
			},
		})
	}

	return {
		sendPendingMessage,
		getUndoMessageFn,
	}
}
